import { useCallback, useEffect, useRef, useState } from 'react';
import { GridStack, type GridItemHTMLElement, type GridStackWidget } from 'gridstack';
import 'gridstack/dist/gridstack.min.css';
import { panelGeometryTarget, panelGeometryNeedsUpdate } from './panelGeometry';
import { PanelGridOptions, PanelLayoutItem, PANEL_DEFAULT_H, PANEL_DEFAULT_W } from './panelGridTypes';
import { attachFitOnDoubleClick, fitPanelElement, growPanelToFitContent } from './panelFit';

export interface UsePanelGridArgs {
  /** The items currently rendered as `.grid-stack-item` children (gs-id = key). */
  items: PanelLayoutItem[];
  /** Build the grid only once the saved layout has loaded or definitively failed. */
  ready: boolean;
  options: PanelGridOptions;
  /**
   * The grid's authoritative geometry after a user action. The caller MUST persist it AND
   * feed it back as `items` — otherwise the next render resets the internal ref and a later
   * change event reports a collapsed tile as expanded at h=1.
   */
  onItemsChange: (items: PanelLayoutItem[]) => void;
}

/**
 * Owns one GridStack instance over a `.grid-stack` container the caller renders.
 *
 * GridStack reads a tile's gs-* attributes only when it ADOPTS the tile, never again. So
 * geometry that changes after init (a late load, a reset, a copied layout) is pushed in by
 * `applyGeometry`, which is idempotent and runs on every `items` change. The 'change'
 * handler refuses to report anything until that first reconcile has happened — otherwise
 * a stray change event would read the pre-load defaults back off the grid and the caller
 * would save them over the user's real arrangement (LC's #33552).
 */
export function usePanelGrid({ items, ready, options, onItemsChange }: UsePanelGridArgs) {
  const gridRef = useRef<HTMLDivElement>(null);
  const gridInstanceRef = useRef<GridStack | null>(null);
  const detachFit = useRef<(() => void) | null>(null);
  const isInitializing = useRef(false);
  const geometryApplied = useRef(false);
  // The 200ms timer that closes the init window. The window covers our own init-time
  // applyGeometry(): a stray `change` the new grid fires in that span must not be reported
  // (isInitializing swallows it). Tracked so a keys-driven rebuild inside those 200ms
  // (e.g. a tile added or removed while the grid is live) can cancel it — otherwise the previous grid's timer
  // would clear the flag early on the new grid and let such a change through.
  const initFlagTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [gridReady, setGridReady] = useState(false);

  // Latest props for handlers that are bound once per grid build.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const onItemsChangeRef = useRef(onItemsChange);
  onItemsChangeRef.current = onItemsChange;

  // WHICH tiles exist (not their sizes). A new or removed tile rebuilds the grid so it is
  // adopted; a move or resize does not.
  const keysSig = items.map((i) => i.key).join('|');

  const applyGeometry = useCallback(() => {
    const grid = gridInstanceRef.current;
    const gridEl = gridRef.current;
    if (!grid || !gridEl) return;
    // Our own grid.update calls emit 'change'; suppress them so reconciling can't save
    // half-applied geometry. Restore rather than clear: during init the flag belongs to
    // the init timer.
    const wasInitializing = isInitializing.current;
    isInitializing.current = true;
    grid.batchUpdate();
    try {
      itemsRef.current.forEach((item) => {
        const el = gridEl.querySelector(`[gs-id="${item.key}"]`) as HTMLElement | null;
        if (!el) return;
        const node = (el as GridItemHTMLElement).gridstackNode;
        const target = panelGeometryTarget(item);
        if (!panelGeometryNeedsUpdate(node, target)) return;
        grid.update(el, target);
      });
    } finally {
      grid.batchUpdate(false);
      isInitializing.current = wasInitializing;
    }
    geometryApplied.current = true;
  }, []);

  // Reconcile whenever the saved side changes (late load, reset, restore, copy).
  useEffect(() => {
    if (gridReady) applyGeometry();
  }, [gridReady, items, applyGeometry]);

  const readItemsFromGrid = useCallback((grid: GridStack): PanelLayoutItem[] => {
    const prevByKey = new Map(itemsRef.current.map((i) => [i.key, i]));
    const out: PanelLayoutItem[] = [];
    grid.getGridItems().forEach((el) => {
      const node = el.gridstackNode;
      const id = node?.id || el.getAttribute('gs-id') || '';
      if (!id) return;
      const x = node?.x ?? 0;
      const y = node?.y ?? 0;
      const w = node?.w ?? PANEL_DEFAULT_W;
      const h = node?.h ?? PANEL_DEFAULT_H;
      if (w < 1 || h < 1) return;
      const prev = prevByKey.get(id);
      const item: PanelLayoutItem = { key: id, x, y, w, h };
      if (prev?.locked) item.locked = true; // toggled elsewhere; preserve it
      if (prev?.collapsed) {
        item.collapsed = true;
        item.h = 1;
        item.expandedH = prev.expandedH ?? (h > 1 ? h : PANEL_DEFAULT_H);
      } else {
        item.expandedH = h; // remember a live resize
      }
      out.push(item);
    });
    return out;
  }, []);

  useEffect(() => {
    if (!ready) return;
    geometryApplied.current = false;
    if (gridInstanceRef.current) {
      gridInstanceRef.current.destroy(false);
      gridInstanceRef.current = null;
    }
    isInitializing.current = true;

    const timer = setTimeout(() => {
      if (!gridRef.current) return;
      const grid = GridStack.init({
        column: options.column,
        cellHeight: options.cellHeight,
        margin: options.margin,
        float: false,
        animate: true,
        handle: options.handle,
        resizable: { handles: 'se' },
      }, gridRef.current);
      gridInstanceRef.current = grid;

      // Double-clicking the se resize handle snaps a tile to fit its content. Shared
      // with LC, which had this inline long before the hook existed.
      detachFit.current?.();
      detachFit.current = attachFitOnDoubleClick(gridRef.current, () => gridInstanceRef.current);

      // Dragging a count panel narrower reflows its blocks into fewer, taller columns.
      // Top the tile up once the drag settles, or the overflow hides behind a scrollbar.
      grid.on('resizestop', (_e, el) => {
        requestAnimationFrame(() => growPanelToFitContent(gridInstanceRef.current, el as HTMLElement));
      });

      grid.on('change', () => {
        if (isInitializing.current) return;
        if (!geometryApplied.current) return;
        onItemsChangeRef.current(readItemsFromGrid(grid));
      });

      applyGeometry();
      setGridReady(true);
      initFlagTimer.current = setTimeout(() => {
        isInitializing.current = false;
        initFlagTimer.current = null;
      }, 200);
    }, 100);

    return () => {
      clearTimeout(timer);
      if (initFlagTimer.current) {
        clearTimeout(initFlagTimer.current);
        initFlagTimer.current = null;
      }
      setGridReady(false);
      geometryApplied.current = false;
      if (gridInstanceRef.current) {
        gridInstanceRef.current.destroy(false);
        gridInstanceRef.current = null;
      }
    };
    // applyGeometry/readItemsFromGrid are stable; items are read through refs on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, keysSig, options.column, options.cellHeight, options.margin, options.handle]);

  /**
   * Mutate one item, push the matching GridStack update, and report the grid's resulting
   * geometry. The ref is updated BEFORE grid.update so both the 'change' echo GridStack
   * fires synchronously from update AND the final read below see the new collapsed/locked
   * flags instead of clobbering them. What gets reported is that final read, not `base`:
   * under float:false the update may have re-packed neighbours, and `base` still carries
   * their pre-compaction y values.
   */
  const updateItem = useCallback((
    key: string,
    mutate: (item: PanelLayoutItem) => void,
    gridOpts: (item: PanelLayoutItem) => GridStackWidget,
  ) => {
    const base = itemsRef.current.map((i) => ({ ...i }));
    const target = base.find((i) => i.key === key);
    if (!target) return;
    mutate(target);
    itemsRef.current = base;
    const grid = gridInstanceRef.current;
    const el = gridRef.current?.querySelector(`[gs-id="${key}"]`) as HTMLElement | null;
    if (grid && el) {
      grid.update(el, gridOpts(target));
      // float:false may have re-packed neighbours; report the grid's real geometry, not `base`.
      // The ref already carries the new collapsed/locked flags, so readItemsFromGrid keeps them.
      onItemsChangeRef.current(readItemsFromGrid(grid));
    } else {
      onItemsChangeRef.current(base);
    }
  }, [readItemsFromGrid]);

  /** Collapse to a header strip (h=1) or expand back to the remembered height. */
  const toggleCollapse = useCallback((key: string) => updateItem(
    key,
    (t) => {
      if (!t.collapsed) {
        t.expandedH = t.h >= 2 ? t.h : (t.expandedH ?? PANEL_DEFAULT_H);
        t.collapsed = true;
        t.h = 1;
      } else {
        t.collapsed = false;
        t.h = t.expandedH ?? PANEL_DEFAULT_H;
        t.expandedH = undefined;
      }
    },
    (t) => ({ h: t.h }),
  ), [updateItem]);

  /**
   * Lock/unlock. `locked` is GridStack's real pin against the packing algorithm;
   * noMove/noResize only take the user's handles away. All three together, or a locked
   * tile still drifts under float:false when a neighbour grows.
   */
  const toggleLock = useCallback((key: string) => updateItem(
    key,
    (t) => { t.locked = !t.locked; },
    (t) => ({ locked: !!t.locked, noMove: !!t.locked, noResize: !!t.locked }),
  ), [updateItem]);

  /** Snap one panel to fit its content. Hosts call this for a first-load auto-fit, or
   *  with `heightOnly` for a panel whose row count swings with its data. */
  const fitPanel = useCallback((key: string, heightOnly = false) => {
    const el = gridRef.current?.querySelector(`[gs-id="${key}"]`) as HTMLElement | null;
    if (el) fitPanelElement(gridInstanceRef.current, gridRef.current, el, heightOnly);
  }, []);

  useEffect(() => () => { detachFit.current?.(); detachFit.current = null; }, []);

  return { gridRef, gridReady, toggleCollapse, toggleLock, fitPanel };
}
